import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotebookPen, Plus, Pencil, Trash2, MapPin, Clock, Loader2, X, Lock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const timeRange = (e) => (e.start_time && e.end_time) ? `${e.start_time} – ${e.end_time}` : (e.start_time || e.end_time || 'No time');
const blank = { log_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', location: '', activity: '' };

const PortalLogbook = () => {
  const [enabled, setEnabled] = useState(null); // null = checking
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [delTarget, setDelTarget] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const acc = await axios.get(`${API}/api/employee-portal/logbook/access`, { headers });
      setEnabled(acc.data.enabled);
      if (acc.data.enabled) {
        const r = await axios.get(`${API}/api/employee-portal/logbook`, { headers });
        setEntries(r.data || []);
      }
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('portal_token'); navigate('/employee-portal/login'); }
      else setEnabled(false);
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(blank); setErr(''); setShowForm(true); };
  const openEdit = (e) => {
    if (e.source === 'admin') return;
    setEditing(e);
    setForm({ log_date: e.log_date, start_time: e.start_time || '', end_time: e.end_time || '', location: e.location || '', activity: e.activity || '' });
    setErr(''); setShowForm(true);
  };
  const save = async () => {
    setErr('');
    if (!form.log_date) { setErr('Pick a date'); return; }
    if (!form.location.trim()) { setErr('Enter a location'); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/api/employee-portal/logbook/${editing.id}`, form, { headers });
      else await axios.post(`${API}/api/employee-portal/logbook`, form, { headers });
      setShowForm(false);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    const e = delTarget; setDelTarget(null);
    try { await axios.delete(`${API}/api/employee-portal/logbook/${e.id}`, { headers }); await load(); }
    catch { /* silent */ }
  };

  if (loading && enabled === null) {
    return <div className="py-16 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>;
  }

  if (enabled === false) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center" data-testid="portal-logbook-disabled">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <Lock className="h-6 w-6 text-gray-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-800">Logbook not enabled</h2>
        <p className="text-sm text-gray-500 mt-1">The Logbook isn't enabled for your account. Please contact HR if you need access.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-logbook-page">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <NotebookPen className="h-6 w-6 text-sky-600" /> My Logbook
          </h1>
          <p className="text-sm text-gray-500">Record where you are during work time.</p>
        </div>
        <Button onClick={openAdd} className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="portal-logbook-add">
          <Plus className="h-4 w-4" /> Add Entry
        </Button>
      </div>

      {entries.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400 text-sm" data-testid="portal-logbook-empty">
          <NotebookPen className="h-10 w-10 mx-auto mb-2 text-gray-200" />
          No entries yet. Tap "Add Entry" to log your first timeline.
        </CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {entries.map((e) => (
            <Card key={e.id} data-testid={`portal-logbook-row-${e.id}`} className="overflow-hidden">
              <CardContent className="p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-sm font-semibold text-gray-900">{fmtDate(e.log_date)}</span>
                      <Badge variant="outline" className="bg-gray-50 text-gray-600 text-[10px]"><Clock className="h-3 w-3 mr-1" />{timeRange(e)}</Badge>
                      {e.source === 'admin' && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Added by HR</Badge>}
                    </div>
                    <p className="text-sm text-gray-700 mt-1.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-sky-500 flex-shrink-0" />{e.location}</p>
                    {e.activity && <p className="text-xs text-gray-500 mt-0.5 pl-5">{e.activity}</p>}
                  </div>
                  {e.source !== 'admin' && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(e)} data-testid={`portal-logbook-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => setDelTarget(e)} data-testid={`portal-logbook-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="portal-logbook-form">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-5 mx-4 max-w-sm w-full z-10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">{editing ? 'Edit Entry' : 'New Entry'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.log_date} onChange={(e) => setForm((p) => ({ ...p, log_date: e.target.value }))} data-testid="portal-logbook-date" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Start time</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))} data-testid="portal-logbook-start" />
                </div>
                <div>
                  <Label className="text-xs">End time</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))} data-testid="portal-logbook-end" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder="Where were you?" data-testid="portal-logbook-location" />
              </div>
              <div>
                <Label className="text-xs">Activity</Label>
                <Textarea value={form.activity} onChange={(e) => setForm((p) => ({ ...p, activity: e.target.value }))} rows={2} placeholder="What were you doing?" data-testid="portal-logbook-activity" />
              </div>
              {err && <p className="text-xs text-red-500">{err}</p>}
              <Button onClick={save} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="portal-logbook-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {editing ? 'Save' : 'Add Entry'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="portal-logbook-delete-confirm">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDelTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-5 mx-4 max-w-xs w-full z-10 text-center">
            <p className="text-sm font-semibold text-gray-900 mb-1">Delete this entry?</p>
            <p className="text-xs text-gray-500 mb-4">{fmtDate(delTarget.log_date)} · {delTarget.location}</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDelTarget(null)}>Cancel</Button>
              <Button className="flex-1 bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalLogbook;
